import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import QuizOptionsModal from './QuizOptionsModal';
import FlashcardOptionsModal from './FlashcardOptionsModal';
import PodcastOptionsModal from './PodcastOptionsModal';
import VideoOptionsModal from './VideoOptionsModal'; // ← Added
import PointsConfirmationModal from './PointsConfirmationModal';
import { QUIZMASTER_API } from '../config';
import '../styles/styles.css';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

export const regenerateFlashcards = async ({
    documentId,
    tokenCount,
    filename,
    setApiStatus,
    startTimer,
    stopTimer,
    setLoading,
    refreshDocuments,
    accessToken,
    supabaseRefreshToken,
    onSignIn,
    maxFlashcards,
    enableOcr,
    difficulty,
    confirmCost,
}) => {
    let timerInterval = null;
    try {
        // Validate tokenCount
        if (typeof tokenCount !== 'number' || tokenCount <= 0) {
            throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
        }

        // Validate maxFlashcards
        if (isNaN(maxFlashcards) || maxFlashcards < 1 || maxFlashcards > 50) {
            throw new Error('Number of flashcards must be between 1 and 50.');
        }

        // Estimate flashcard generation cost
        setApiStatus('Estimating flashcard generation cost...');
        const costResponse = await axios.post(
            `${QUIZMASTER_API}/estimate-flashcard-generation-cost/?document_id=${documentId}&max_flashcards=${maxFlashcards}&enable_ocr=${enableOcr}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        const { estimated_points } = costResponse.data.estimation;
        const shouldProceed = await Promise.resolve(
            confirmCost
                ? confirmCost({ estimatedPoints: estimated_points, numFlashcards: maxFlashcards })
                : window.confirm(
                    `Generating ${maxFlashcards} flashcards for this document will cost approximately ${estimated_points} points. Proceed?`
                )
        );
        if (!shouldProceed) {
            throw new Error('User canceled');
        }

        setLoading(true);
        timerInterval = startTimer();

        // Call generate-flashcards endpoint
        setApiStatus('Generating flashcards...');
        console.log('[regenerateFlashcards] API call with difficulty:', difficulty);
        let response = await axios.post(
            `${QUIZMASTER_API}/generate-flashcards/?document_id=${documentId}&max_flashcards=${maxFlashcards}&enable_ocr=${enableOcr}&difficulty=${difficulty}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        if (response.status === 401) {
            setApiStatus('Refreshing token...');
            const refreshResponse = await axios.post(
                `${QUIZMASTER_API}/auth/refresh`,
                { refresh_token: supabaseRefreshToken },
                { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
            );
            const newAccessToken = refreshResponse.data.access_token;
            const newRefreshToken = refreshResponse.data.refresh_token;
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });

            response = await axios.post(
                `${QUIZMASTER_API}/generate-flashcards/?document_id=${documentId}&max_flashcards=${maxFlashcards}&enable_ocr=${enableOcr}&difficulty=${difficulty}`,
                {},
                { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
            );
        }

        stopTimer(timerInterval);
        setApiStatus('Flashcard generation completed!');
        setLoading(false);
        console.log('[RegenerateFlashcards] Calling refreshDocuments with:', { id: documentId, filename, token_count: tokenCount });
        refreshDocuments({ id: documentId, filename, token_count: tokenCount });
    } catch (error) {
        if (timerInterval) stopTimer(timerInterval);
        setLoading(false);
        if (error.message !== 'User canceled') {
            let errorMessage = 'An error occurred while generating the flashcards. Please try again.';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail; // Use backend's error message directly
            } else if (error.response) {
                errorMessage = `Request failed with status code ${error.response.status}. Please try again.`;
            } else {
                errorMessage = error.message || errorMessage;
            }
            setApiStatus(errorMessage); // Reflect backend message in UI
            console.error('[RegenerateFlashcards Error]', error, { documentId, tokenCount, filename });
            alert(errorMessage); // Show backend message in alert
        }
    }
};

export const regenerateQuiz = async ({
    documentId,
    tokenCount,
    filename,
    setApiStatus,
    startTimer,
    stopTimer,
    setLoading,
    refreshDocuments,
    accessToken,
    supabaseRefreshToken,
    onSignIn,
    quizType,
    numQuestions,
    userId,
    confirmCost,
}) => {
    let timerInterval = null;
    try {
        // Validate tokenCount
        if (typeof tokenCount !== 'number' || tokenCount <= 0) {
            throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
        }

        // Validate quizType and numQuestions
        if (!['standard', 'adaptive'].includes(quizType)) {
            throw new Error('Invalid quiz type. Must be "standard" or "adaptive".');
        }
        if (isNaN(numQuestions) || numQuestions < 1 || numQuestions > 50) {
            throw new Error('Number of questions must be between 1 and 50.');
        }

        // Estimate question generation cost
        setApiStatus('Estimating question generation cost...');
        const costResponse = await axios.post(
            `${QUIZMASTER_API}/estimate-question-generation-cost/?document_id=${documentId}&max_questions=${numQuestions}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        const { estimated_points } = costResponse.data.estimation;
        const shouldProceed = await Promise.resolve(
            confirmCost
                ? confirmCost({ estimatedPoints: estimated_points, numQuestions })
                : window.confirm(
                    `Generating ${numQuestions} questions for this quiz will cost approximately ${estimated_points} points. Proceed?`
                )
        );
        if (!shouldProceed) {
            throw new Error('User canceled');
        }

        setLoading(true);
        timerInterval = startTimer();

        // Call generate-questions endpoint
        setApiStatus('Generating questions...');
        let response = await axios.post(
            `${QUIZMASTER_API}/generate-questions/?document_id=${documentId}&max_questions=${numQuestions}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        if (response.status === 401) {
            setApiStatus('Refreshing token...');
            const refreshResponse = await axios.post(
                `${QUIZMASTER_API}/auth/refresh`,
                { refresh_token: supabaseRefreshToken },
                { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
            );
            const newAccessToken = refreshResponse.data.access_token;
            const newRefreshToken = refreshResponse.data.refresh_token;
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });

            response = await axios.post(
                `${QUIZMASTER_API}/generate-questions/?document_id=${documentId}&max_questions=${numQuestions}`,
                {},
                { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
            );
        }

        setApiStatus('Creating quiz...');
        const createQuizResponse = await axios.post(
            `${QUIZMASTER_API}/create-quiz/?document_id=${documentId}&num_questions=${numQuestions}&quiz_type=${quizType}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );
        const quizId = createQuizResponse.data.quiz_id;

        setApiStatus('Creating quiz instance...');
        const createQuizInstanceResponse = await axios.post(
            `${QUIZMASTER_API}/create-quiz-instance/?quiz_id=${quizId}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );
        const instanceId = createQuizInstanceResponse.data.instance_id;

        setApiStatus('Fetching quiz instance details...');
        await axios.get(
            `${QUIZMASTER_API}/get-quiz-instance-details/?instance_id=${instanceId}`,
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        stopTimer(timerInterval);
        setApiStatus('All tasks completed!');
        setLoading(false);
        console.log('[RegenerateQuiz] Calling refreshDocuments with:', { id: documentId, filename, token_count: tokenCount });
        refreshDocuments({ id: documentId, filename, token_count: tokenCount });
    } catch (error) {
        if (timerInterval) stopTimer(timerInterval);
        setLoading(false);
        if (error.message !== 'User canceled') {
            let errorMessage = 'An error occurred while generating the quiz. Please try again.';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail; // Use backend's error message directly
            } else if (error.response) {
                errorMessage = `Request failed with status code ${error.response.status}. Please try again.`;
            } else {
                errorMessage = error.message || errorMessage;
            }
            setApiStatus(errorMessage); // Reflect backend message in UI
            console.error('[RegenerateQuiz Error]', error, { documentId, tokenCount, filename });
            alert(errorMessage); // Show backend message in alert
        }
    }
};

export const regeneratePodcast = async ({
    documentId,
    tokenCount,
    filename,
    setApiStatus,
    startTimer,
    stopTimer,
    setLoading,
    refreshDocuments,
    accessToken,
    supabaseRefreshToken,
    onSignIn,
    durationMinutes,
    userId,
    confirmCost,
}) => {
    let timerInterval = null;
    try {
        // Validate tokenCount
        if (typeof tokenCount !== 'number' || tokenCount <= 0) {
            throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
        }

        // Validate durationMinutes
        if (isNaN(durationMinutes) || durationMinutes < 1 || durationMinutes > 10) {
            throw new Error('Duration must be between 1 and 10 minutes.');
        }

        // Estimate podcast generation cost
        setApiStatus('Estimating podcast generation cost...');
        const costResponse = await axios.post(
            `${QUIZMASTER_API}/estimate-podcast-generation-cost/?document_id=${documentId}&duration_minutes=${durationMinutes}&enable_ocr=true`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        const { estimated_points } = costResponse.data.estimation;
        const shouldProceed = await Promise.resolve(
            confirmCost
                ? confirmCost({ estimatedPoints: estimated_points, durationMinutes })
                : window.confirm(
                    `Generating a ${durationMinutes}-minute podcast will cost approximately ${estimated_points} points. Proceed?`
                )
        );
        if (!shouldProceed) {
            throw new Error('User canceled');
        }

        setLoading(true);
        timerInterval = startTimer();

        // Generate podcast
        setApiStatus('Generating podcast...');
        let response = await axios.post(
            `${QUIZMASTER_API}/generate-podcast/?document_id=${documentId}&duration_minutes=${durationMinutes}&enable_ocr=true`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        if (response.status === 401) {
            setApiStatus('Refreshing token...');
            const refreshResponse = await axios.post(
                `${QUIZMASTER_API}/auth/refresh`,
                { refresh_token: supabaseRefreshToken },
                { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
            );
            const newAccessToken = refreshResponse.data.access_token;
            const newRefreshToken = refreshResponse.data.refresh_token;
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });

            response = await axios.post(
                `${QUIZMASTER_API}/generate-podcast/?document_id=${documentId}&duration_minutes=${durationMinutes}&enable_ocr=true`,
                {},
                { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
            );
        }

        stopTimer(timerInterval);
        setApiStatus('Podcast generated successfully!');
        setLoading(false);
        console.log('[RegeneratePodcast] Calling refreshDocuments with:', { id: documentId, filename, token_count: tokenCount });
        refreshDocuments({ id: documentId, filename, token_count: tokenCount });
    } catch (error) {
        if (timerInterval) stopTimer(timerInterval);
        setLoading(false);
        if (error.message !== 'User canceled') {
            let errorMessage = 'An error occurred while generating the podcast. Please try again.';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail; // Use backend's error message directly
            } else if (error.response) {
                errorMessage = `Request failed with status code ${error.response.status}. Please try again.`;
            } else {
                errorMessage = error.message || errorMessage;
            }
            setApiStatus(errorMessage); // Reflect backend message in UI
            console.error('[RegeneratePodcast Error]', error, { documentId, tokenCount, filename });
            alert(errorMessage); // Show backend message in alert
        }
    }
};

export const regenerateVideo = async ({
    documentId,
    tokenCount,
    filename,
    setApiStatus,
    startTimer,
    stopTimer,
    setLoading,
    refreshDocuments,
    accessToken,
    supabaseRefreshToken,
    onSignIn,
    durationMinutes = 1,
    confirmCost,
}) => {
    let timerInterval = null;
    try {
        if (typeof tokenCount !== 'number' || tokenCount <= 0) {
            throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
        }
        if (isNaN(durationMinutes) || durationMinutes < 1 || durationMinutes > 30) {
            throw new Error('Video duration must be between 1 and 30 minutes.');
        }

        // 1. Estimate cost (query params are correct here)
        setApiStatus('Estimating video generation cost...');
        const costResponse = await axios.post(
            `${QUIZMASTER_API}/videos/estimate-video-generation-cost/?document_id=${documentId}&duration_minutes=${durationMinutes}`,
            {},
            { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
        );

        const { estimated_points } = costResponse.data.estimation;

        const shouldProceed = await Promise.resolve(
            confirmCost
                ? confirmCost({ estimatedPoints: estimated_points, durationMinutes })
                : window.confirm(`Generating a ${durationMinutes}-minute video will cost approximately ${estimated_points} points. Proceed?`)
        );

        if (!shouldProceed) throw new Error('User canceled');

        // 2. Generate video — JSON body, no query params
        setLoading(true);
        timerInterval = startTimer();
        setApiStatus('Generating video... This may take several minutes.');

        let response = await axios.post(
            `${QUIZMASTER_API}/videos/generate/`,  // ← Correct endpoint
            {
                document_id: documentId,
                title: filename || "My AI Video"  // Optional: use filename as title
            },
            {
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            }
        );

        // 3. Handle token refresh if needed
        if (response.status === 401) {
            setApiStatus('Refreshing token...');
            const refreshResponse = await axios.post(
                `${QUIZMASTER_API}/auth/refresh`,
                { refresh_token: supabaseRefreshToken },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const newAccessToken = refreshResponse.data.access_token;
            const newRefreshToken = refreshResponse.data.refresh_token;
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });

            // Retry generation with new token
            response = await axios.post(
                `${QUIZMASTER_API}/videos/generate/`,
                {
                    document_id: documentId,
                    title: filename || "My AI Video"
                },
                {
                    headers: {
                        'accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${newAccessToken}`
                    }
                }
            );
        }

        stopTimer(timerInterval);
        setApiStatus('Video generation started! Check your library soon.');
        setLoading(false);
        refreshDocuments({ id: documentId, filename, token_count: tokenCount });

    } catch (error) {
        if (timerInterval) stopTimer(timerInterval);
        setLoading(false);

        if (error.message !== 'User canceled') {
            let errorMessage = 'An error occurred while generating the video. Please try again.';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response) {
                errorMessage = `Request failed with status code ${error.response.status}.`;
            } else {
                errorMessage = error.message || errorMessage;
            }
            setApiStatus(errorMessage);
            console.error('[RegenerateVideo Error]', error);
            alert(errorMessage);
        }
    }
};

const FileUploader = ({
    file,
    setApiStatus,
    startTimer,
    stopTimer,
    setLoading,
    userId,
    refreshDocuments,
    accessToken,
    supabaseRefreshToken,
    onSignIn,
    generationType = 'quiz',
    customButton,
}) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const uploadPromiseRef = useRef(null);
    const costConfirmationRef = useRef(null);
    const [costConfirmation, setCostConfirmation] = useState(null);

    const uploadFile = useCallback(async () => {
        if (!file?.[0]) {
            setApiStatus('Please select a PDF file before uploading.');
            return;
        }

        if (file.length > 1) {
            const message = 'Please upload only one PDF at a time.';
            setApiStatus(message);
            alert(message);
            return;
        }

        const selectedFile = file[0];
        const isPdf = selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            const message = 'Only PDF files are supported.';
            setApiStatus(message);
            alert(message);
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            const message = 'Maximum file size is 25 MB.';
            setApiStatus(message);
            alert(message);
            return;
        }

        let timerInterval = null;
        let document_id, token_count, filename, created_at;
        try {
            if (generationType === 'flashcards') {
                setApiStatus('Opening flashcard options...');
                uploadPromiseRef.current = {};
                setRegenerateModalVisible(true);
                const { maxFlashcards, enableOcr, difficulty } = await new Promise((resolve, reject) => {
                    uploadPromiseRef.current.resolve = resolve;
                    uploadPromiseRef.current.reject = reject;
                });

                // Rest of flashcard generation logic will be added below
                // For now, continue with upload and then generate flashcards
                setApiStatus('Uploading file...');
                const formData = new FormData();
                formData.append('file', selectedFile);

                const uploadResponse = await axios.post(
                    `${QUIZMASTER_API}/upload-pdf/`,
                    formData,
                    {
                        headers: {
                            'accept': 'application/json',
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                ({ document_id, token_count, filename, created_at } = uploadResponse.data);
                console.log('[FileUploader] Upload response:', { document_id, token_count, filename, created_at });
                if (!document_id || !filename) {
                    throw new Error('Invalid response from upload-pdf endpoint: missing document_id or filename');
                }
                if (typeof token_count !== 'number' || token_count <= 0) {
                    throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
                }

                // Estimate flashcard generation cost
                setApiStatus('Estimating flashcard generation cost...');
                const costResponse = await axios.post(
                    `${QUIZMASTER_API}/estimate-flashcard-generation-cost/?document_id=${document_id}&max_flashcards=${maxFlashcards}&enable_ocr=${enableOcr}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                const { estimated_points } = costResponse.data.estimation;
                setCostConfirmation({ estimatedPoints: estimated_points, numFlashcards: maxFlashcards });
                const shouldProceed = await new Promise((resolve) => {
                    costConfirmationRef.current = resolve;
                });

                if (!shouldProceed) {
                    throw new Error('User canceled');
                }

                setCostConfirmation(null);
                costConfirmationRef.current = null;

                timerInterval = startTimer();

                // Call generate-flashcards endpoint
                setApiStatus('Generating flashcards...');
                let generateFlashcardsResponse = await axios.post(
                    `${QUIZMASTER_API}/generate-flashcards/?document_id=${document_id}&max_flashcards=${maxFlashcards}&enable_ocr=${enableOcr}&difficulty=${difficulty}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                if (generateFlashcardsResponse.status === 401) {
                    setApiStatus('Refreshing token...');
                    const refreshResponse = await axios.post(
                        `${QUIZMASTER_API}/auth/refresh`,
                        { refresh_token: supabaseRefreshToken },
                        { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
                    );
                    const newAccessToken = refreshResponse.data.access_token;
                    const newRefreshToken = refreshResponse.data.refresh_token;
                    onSignIn({ user_id: userId, accessToken: newAccessToken, refreshToken: newRefreshToken });

                    generateFlashcardsResponse = await axios.post(
                        `${QUIZMASTER_API}/generate-flashcards/?document_id=${document_id}&max_flashcards=${maxFlashcards}&enable_ocr=${enableOcr}&difficulty=${difficulty}`,
                        {},
                        { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
                    );
                }

                stopTimer(timerInterval);
                setApiStatus('Flashcard generation completed!');
                setLoading(false);
                console.log('[FileUploader] Calling refreshDocuments with:', { id: document_id, filename, token_count });
                refreshDocuments({ id: document_id, filename, token_count });
                return;
            } else if (generationType === 'podcast') {
                setApiStatus('Opening podcast options...');
                uploadPromiseRef.current = {};
                setRegenerateModalVisible(true);
                const { durationMinutes } = await new Promise((resolve, reject) => {
                    uploadPromiseRef.current.resolve = resolve;
                    uploadPromiseRef.current.reject = reject;
                });

                setLoading(true);
                setApiStatus('Uploading file...');
                const formData = new FormData();
                formData.append('file', selectedFile);

                const uploadResponse = await axios.post(
                    `${QUIZMASTER_API}/upload-pdf/`,
                    formData,
                    {
                        headers: {
                            'accept': 'application/json',
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                ({ document_id, token_count, filename, created_at } = uploadResponse.data);
                console.log('[FileUploader] Upload response:', { document_id, token_count, filename, created_at });
                if (!document_id || !filename) {
                    throw new Error('Invalid response from upload-pdf endpoint: missing document_id or filename');
                }
                if (typeof token_count !== 'number' || token_count <= 0) {
                    throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
                }

                // Estimate podcast generation cost
                setApiStatus('Estimating podcast generation cost...');
                const costResponse = await axios.post(
                    `${QUIZMASTER_API}/estimate-podcast-generation-cost/?document_id=${document_id}&duration_minutes=${durationMinutes}&enable_ocr=true`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                const { estimated_points } = costResponse.data.estimation;
                setApiStatus('Confirm podcast generation to continue.');
                setCostConfirmation({ estimatedPoints: estimated_points, durationMinutes });
                const shouldProceed = await new Promise((resolve) => {
                    costConfirmationRef.current = resolve;
                });

                if (!shouldProceed) {
                    throw new Error('User canceled');
                }

                setCostConfirmation(null);
                costConfirmationRef.current = null;

                timerInterval = startTimer();

                // Generate podcast
                setApiStatus('Generating podcast...');
                let generatePodcastResponse = await axios.post(
                    `${QUIZMASTER_API}/generate-podcast/?document_id=${document_id}&duration_minutes=${durationMinutes}&enable_ocr=true`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                if (generatePodcastResponse.status === 401) {
                    setApiStatus('Refreshing token...');
                    const refreshResponse = await axios.post(
                        `${QUIZMASTER_API}/auth/refresh`,
                        { refresh_token: supabaseRefreshToken },
                        { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
                    );
                    const newAccessToken = refreshResponse.data.access_token;
                    const newRefreshToken = refreshResponse.data.refresh_token;
                    onSignIn({ user_id: userId, accessToken: newAccessToken, refreshToken: newRefreshToken });

                    generatePodcastResponse = await axios.post(
                        `${QUIZMASTER_API}/generate-podcast/?document_id=${document_id}&duration_minutes=${durationMinutes}&enable_ocr=true`,
                        {},
                        { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
                    );
                }

                stopTimer(timerInterval);
                setApiStatus('Podcast generation completed!');
                setLoading(false);
                console.log('[FileUploader] Calling refreshDocuments with:', { id: document_id, filename, token_count });
                refreshDocuments({ id: document_id, filename, token_count });
                return;
            } else if (generationType === 'video') {
                // Skip options modal and default to 1 minute
                const durationMinutes = 1;

                setLoading(true);
                setApiStatus('Uploading file...');
                const formData = new FormData();
                formData.append('file', selectedFile);

                const uploadResponse = await axios.post(
                    `${QUIZMASTER_API}/upload-pdf/`,
                    formData,
                    {
                        headers: {
                            'accept': 'application/json',
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                ({ document_id, token_count, filename, created_at } = uploadResponse.data);
                if (typeof token_count !== 'number' || token_count <= 0) {
                    throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
                }

                setApiStatus('Estimating video generation cost...');
                const costResponse = await axios.post(
                    `${QUIZMASTER_API}/videos/estimate-video-generation-cost/?document_id=${document_id}&duration_minutes=${durationMinutes}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                const { estimated_points } = costResponse.data.estimation;
                setCostConfirmation({ estimatedPoints: estimated_points, durationMinutes });
                const shouldProceed = await new Promise((resolve) => {
                    costConfirmationRef.current = resolve;
                });

                if (!shouldProceed) throw new Error('User canceled');

                setCostConfirmation(null);
                costConfirmationRef.current = null;
                timerInterval = startTimer();
                setApiStatus('Generating video...');
                await axios.post(`${QUIZMASTER_API}/videos/generate/`, { document_id, title: filename || "My AI Video" }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` } });
                stopTimer(timerInterval);
                setApiStatus('Video generation started!');
                setLoading(false);
                refreshDocuments({ id: document_id, filename, token_count });
                return;
            } else {
                // Quiz generation logic
                setApiStatus('Opening quiz options...');
                uploadPromiseRef.current = {};
                setModalVisible(true);
                const { quizType, numQuestions } = await new Promise((resolve, reject) => {
                    uploadPromiseRef.current.resolve = resolve;
                    uploadPromiseRef.current.reject = reject;
                });

                if (!['standard', 'adaptive'].includes(quizType)) {
                    throw new Error('Invalid quiz type. Must be "standard" or "adaptive".');
                }
                if (isNaN(numQuestions) || numQuestions < 1 || numQuestions > 50) {
                    throw new Error('Number of questions must be between 1 and 50.');
                }

                setLoading(true);
                setApiStatus('Uploading file...');
                const formData = new FormData();
                formData.append('file', selectedFile);

                const uploadResponse = await axios.post(
                    `${QUIZMASTER_API}/upload-pdf/`,
                    formData,
                    {
                        headers: {
                            'accept': 'application/json',
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                ({ document_id, token_count, filename, created_at } = uploadResponse.data);
                console.log('[FileUploader] Upload response:', { document_id, token_count, filename, created_at });
                if (!document_id || !filename) {
                    throw new Error('Invalid response from upload-pdf endpoint: missing document_id or filename');
                }
                if (typeof token_count !== 'number' || token_count <= 0) {
                    throw new Error('The uploaded file could not be processed. Please ensure it contains readable text and try again.');
                }

                // Estimate question generation cost
                setApiStatus('Estimating question generation cost...');
                const costResponse = await axios.post(
                    `${QUIZMASTER_API}/estimate-question-generation-cost/?document_id=${document_id}&max_questions=${numQuestions}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                const { estimated_points } = costResponse.data.estimation;
                setApiStatus('Confirm quiz generation to continue.');
                setCostConfirmation({ estimatedPoints: estimated_points, numQuestions });
                const shouldProceed = await new Promise((resolve) => {
                    costConfirmationRef.current = resolve;
                });

                if (!shouldProceed) {
                    throw new Error('User canceled');
                }

                setCostConfirmation(null);
                costConfirmationRef.current = null;

                timerInterval = startTimer();

                // Call generate-questions endpoint
                setApiStatus('Generating questions...');
                let generateQuestionsResponse = await axios.post(
                    `${QUIZMASTER_API}/generate-questions/?document_id=${document_id}&max_questions=${numQuestions}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                if (generateQuestionsResponse.status === 401) {
                    setApiStatus('Refreshing token...');
                    const refreshResponse = await axios.post(
                        `${QUIZMASTER_API}/auth/refresh`,
                        { refresh_token: supabaseRefreshToken },
                        { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
                    );
                    const newAccessToken = refreshResponse.data.access_token;
                    const newRefreshToken = refreshResponse.data.refresh_token;
                    onSignIn({ user_id: userId, accessToken: newAccessToken, refreshToken: newRefreshToken });

                    generateQuestionsResponse = await axios.post(
                        `${QUIZMASTER_API}/generate-questions/?document_id=${document_id}&max_questions=${numQuestions}`,
                        {},
                        { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
                    );
                }

                setApiStatus('Creating quiz...');
                const createQuizResponse = await axios.post(
                    `${QUIZMASTER_API}/create-quiz/?document_id=${document_id}&num_questions=${numQuestions}&quiz_type=${quizType}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );
                const quizId = createQuizResponse.data.quiz_id;

                setApiStatus('Creating quiz instance...');
                const createQuizInstanceResponse = await axios.post(
                    `${QUIZMASTER_API}/create-quiz-instance/?quiz_id=${quizId}`,
                    {},
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );
                const instanceId = createQuizInstanceResponse.data.instance_id;

                setApiStatus('Fetching quiz instance details...');
                await axios.get(
                    `${QUIZMASTER_API}/get-quiz-instance-details/?instance_id=${instanceId}`,
                    { headers: { 'accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
                );

                stopTimer(timerInterval);
                setApiStatus('All tasks completed!');
                setLoading(false);
                console.log('[FileUploader] Calling refreshDocuments with:', { id: document_id, filename, token_count, created_at });
                refreshDocuments({ id: document_id, filename, token_count, created_at });
            }
        } catch (error) {
            if (timerInterval) stopTimer(timerInterval);
            setLoading(false);
            setCostConfirmation(null);
            costConfirmationRef.current = null;
            if (error.message === 'User canceled') {
                console.log('[FileUploader] Upload canceled by user');
                setApiStatus('Upload canceled.');
            } else {
                let errorMessage = 'An error occurred while processing the file. Please try again.';
                if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail; // Use backend's error message directly
                } else if (error.response) {
                    errorMessage = `Request failed with status code ${error.response.status}. Please try again.`;
                } else {
                    errorMessage = error.message || errorMessage;
                }
                setApiStatus(errorMessage); // Reflect backend message in UI
                console.error('[UploadFile Error]', error, { document_id, token_count, filename, created_at });
                alert(errorMessage); // Show backend message in alert
            }
        }
    }, [file, userId, accessToken, supabaseRefreshToken, onSignIn, setLoading, setApiStatus, startTimer, stopTimer, refreshDocuments, generationType]);

    const handleCostConfirm = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationRef.current) {
            costConfirmationRef.current(true);
            costConfirmationRef.current = null;
        }
    }, []);

    const handleCostCancel = useCallback(() => {
        setCostConfirmation(null);
        setApiStatus('Upload canceled.');
        setLoading(false); // Ensure spinner stops
        if (costConfirmationRef.current) {
            costConfirmationRef.current(false);
            costConfirmationRef.current = null;
        }
    }, []);

    return (
        <>
            {customButton(uploadFile)}
            <QuizOptionsModal
                visible={modalVisible}
                onConfirm={(options) => {
                    setModalVisible(false);
                    uploadPromiseRef.current?.resolve(options);
                    uploadPromiseRef.current = null;
                }}
                onCancel={() => {
                    setModalVisible(false);
                    uploadPromiseRef.current?.reject(new Error('User canceled'));
                    uploadPromiseRef.current = null;
                }}
            />
            <FlashcardOptionsModal
                visible={regenerateModalVisible && generationType === 'flashcards'}
                onConfirm={(options) => {
                    setRegenerateModalVisible(false);
                    uploadPromiseRef.current?.resolve(options);
                    uploadPromiseRef.current = null;
                }}
                onCancel={() => {
                    setRegenerateModalVisible(false);
                    uploadPromiseRef.current?.reject(new Error('User canceled'));
                    uploadPromiseRef.current = null;
                }}
            />
            <PodcastOptionsModal
                visible={regenerateModalVisible && generationType === 'podcast'}
                onConfirm={(options) => {
                    setRegenerateModalVisible(false);
                    uploadPromiseRef.current?.resolve(options);
                    uploadPromiseRef.current = null;
                }}
                onCancel={() => {
                    setRegenerateModalVisible(false);
                    uploadPromiseRef.current?.reject(new Error('User canceled'));
                    uploadPromiseRef.current = null;
                }}
            />
            <VideoOptionsModal
                visible={regenerateModalVisible && generationType === 'video'}
                onConfirm={(options) => {
                    setRegenerateModalVisible(false);
                    uploadPromiseRef.current?.resolve(options);
                }}
                onCancel={() => {
                    setRegenerateModalVisible(false);
                    uploadPromiseRef.current?.reject(new Error('User canceled'));
                }} ref
            />
            <PointsConfirmationModal
                visible={Boolean(costConfirmation)}
                estimatedPoints={costConfirmation?.estimatedPoints ?? 0}
                numQuestions={costConfirmation?.numQuestions ?? costConfirmation?.numFlashcards ?? costConfirmation?.durationMinutes ?? 0}
                onConfirm={handleCostConfirm}
                onCancel={handleCostCancel}
                title={generationType === 'video' ? 'Confirm video generation' : generationType === 'podcast' ? 'Confirm podcast generation' : costConfirmation?.numFlashcards ? 'Confirm flashcard generation' : 'Confirm quiz generation'}
                questionText={costConfirmation?.numFlashcards ? "flashcards" : generationType === 'video' ? "minutes of video" : generationType === 'podcast' ? "minutes of podcast" : "questions"}
            />
        </>
    );
};

export default FileUploader;